/** Slide-in sheet panel with backdrop blur. Uses React Aria for accessibility. */
import {
  Dialog as AriaDialog,
  Modal,
  ModalOverlay,
  Heading,
  Button as AriaButton,
} from 'react-aria-components';
import { X, type LucideIcon } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';
import { useInterfacePortalContainer } from '@/foundation/ui/InterfacePortalContext';

const OVERLAY_ANIMATION = 'f-overlay-in 200ms cubic-bezier(0.16, 1, 0.3, 1) forwards';
const OVERLAY_EXIT = 'f-overlay-out 150ms cubic-bezier(0.16, 1, 0.3, 1) forwards';

const SHEET_ANIMATIONS = {
  right: {
    in: 'f-sheet-in-right 250ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
    out: 'f-sheet-out-right 200ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
  },
  left: {
    in: 'f-sheet-in-left 250ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
    out: 'f-sheet-out-left 200ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
  },
} as const;

const SIZE_WIDTHS: Record<SheetSize, string> = {
  sm: '380px',
  md: '480px',
  lg: '600px',
};

type SheetSize = 'sm' | 'md' | 'lg';
type SheetSide = 'right' | 'left';

export interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon?: LucideIcon;
  children: React.ReactNode;
  size?: SheetSize;
  side?: SheetSide;
  zIndex?: number;
  className?: string;
}

export function Sheet({
  isOpen,
  onClose,
  title,
  icon: Icon,
  children,
  size = 'md',
  side = 'right',
  zIndex = 50,
  className,
}: SheetProps) {
  const portalContainer = useInterfacePortalContainer();
  const animations = SHEET_ANIMATIONS[side];

  return (
    <ModalOverlay
      isOpen={isOpen}
      UNSTABLE_portalContainer={portalContainer ?? undefined}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      isDismissable
      className={cn('fixed inset-0 flex', side === 'right' ? 'justify-end' : 'justify-start')}
      style={({ isEntering, isExiting }) => ({
        zIndex,
        background: 'var(--f-bg-overlay)',
        backdropFilter: 'blur(4px)',
        animation: isEntering ? OVERLAY_ANIMATION : isExiting ? OVERLAY_EXIT : undefined,
      })}
    >
      <Modal
        className="h-full outline-none w-full sm:w-auto"
        style={({ isEntering, isExiting }) => ({
          maxWidth: SIZE_WIDTHS[size],
          animation: isEntering ? animations.in : isExiting ? animations.out : undefined,
        })}
      >
        <AriaDialog
          className={cn('flex h-full flex-col outline-none', className)}
          style={{
            background: 'var(--f-bg-raised)',
            borderLeft: side === 'right' ? '1px solid var(--f-border)' : undefined,
            borderRight: side === 'left' ? '1px solid var(--f-border)' : undefined,
            boxShadow: 'var(--f-shadow-lg)',
          }}
        >
          {/* Header */}
          <div
            className="flex shrink-0 items-center gap-3 px-5 py-4"
            style={{ borderBottom: '1px solid var(--f-border)' }}
          >
            {Icon && (
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{
                  background: 'var(--f-accent-subtle)',
                  color: 'var(--f-accent-text)',
                }}
              >
                <Icon size={16} strokeWidth={2} />
              </div>
            )}
            <Heading
              slot="title"
              className="flex-1 font-semibold"
              style={{
                fontSize: 'var(--f-text-lg)',
                color: 'var(--f-fg)',
                fontFamily: 'var(--f-font-display)',
              }}
            >
              {title}
            </Heading>
            <AriaButton
              onPress={() => onClose()}
              className="flex h-7 w-7 items-center justify-center rounded-lg transition-all duration-150 cursor-pointer"
              style={{ background: 'var(--f-bg-sunken)', color: 'var(--f-fg-muted)' }}
              aria-label="Close"
            >
              <X size={14} strokeWidth={2.5} />
            </AriaButton>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}
